import { Build } from '@mui/icons-material';
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TransportGroundSensorIcon, TransportTrailerIcon } from 'app/icons';
import React, { forwardRef } from 'react';

import { GUARD_ACTIONS, GUARD_BIT_INDICES } from '../TabPanelCondition.constants';
import { GuardState } from '../TabPanelCondition.types';
import { isCoverOpen } from '../TabPanelCondition.utils';

const ClampIndicator = styled(Box)(({ color }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: color,
  flexShrink: 0,
}));

const IconButtonComponent = forwardRef((props, ref) => <div ref={ref} {...props} />);

export const TrailerSection = ({
  transportItem,
  conditionItem,
  galileoskyDevice,
  guardData,
  sensorDisabled,
  fuelLevelPermission,
  onSensorClick,
  config,
}) => {
  const { sensor_sn, uza_status, dun_cover } = conditionItem.data;
  const guardStatusTrailerBit = guardData.guardStatus[GUARD_BIT_INDICES.TRAILER];
  const guardOutTrailerBit = guardData.guardOut[GUARD_BIT_INDICES.TRAILER];

  // Определяем цвет для серийного номера сенсора
  const isSensorMismatch =
    fuelLevelPermission && sensor_sn !== config.conditions.sensor_sn.default && sensor_sn !== transportItem.trailer_fuel_level_sensor;

  // Форматирование серийного номера
  const sensorSnFormatted = galileoskyDevice ? (sensor_sn !== config.conditions.sensor_sn.default ? `№ ${sensor_sn}` : 'N/a') : '-';

  // Определение цвета иконки прицепа
  const trailerIconColor = guardStatusTrailerBit === GuardState.LOCKED ? 'error' : 'inherit';

  // Проверка возможности управления
  const isControlDisabled = !galileoskyDevice || config.transportDeviceTypes[transportItem.device_type] !== 'galileosky';

  return (
    <Paper variant='outlined'>
      <Typography className='MuiPaper-label' variant='body1'>
        Прицеп
      </Typography>

      <Stack className='MuiPaperProgress-root'>
        {/* Серийный номер датчика */}
        <div className='MuiPaperProgress-cover'>
          <Typography
            className='MuiPaperProgress-degrees'
            variant='caption'
            sx={(theme) => ({
              ...(isSensorMismatch && {
                color: theme.palette.danger.main,
              }),
            })}>
            {sensorSnFormatted}
          </Typography>
        </div>

        {/* Иконка прицепа */}
        <div className='MuiPaperProgress-determinate'>
          {galileoskyDevice && guardData.guardStatusNumber !== config.conditions.guard_status.default ? (
            <TransportTrailerIcon
              viewBox='0 0 64 64'
              className={`MuiPaperProgress-color${trailerIconColor.charAt(0).toUpperCase() + trailerIconColor.slice(1)}`}
            />
          ) : (
            <TransportTrailerIcon viewBox='0 0 64 64' disabled />
          )}
        </div>

        {/* Датчик заземления */}
        {Boolean(transportItem.ground_sensor) && <GroundSensor uzaStatus={uza_status} dunCover={dun_cover} config={config} />}

        {/* Кнопка управления прицепом */}
        <div className='MuiPaperProgress-button'>
          {galileoskyDevice && guardData.guardOutNumber !== config.conditions.guard_out.default ? (
            <TrailerControlButton
              isActive={guardOutTrailerBit === GuardState.LOCKED}
              disabled={isControlDisabled || sensorDisabled}
              sensorDisabled={sensorDisabled}
              onToggle={() =>
                onSensorClick(guardOutTrailerBit === GuardState.LOCKED ? GUARD_ACTIONS.TRAILER_LOCK : GUARD_ACTIONS.TRAILER_UNLOCK)
              }
            />
          ) : (
            <IconButton size='small' disabled>
              <Build />
            </IconButton>
          )}
        </div>
      </Stack>
    </Paper>
  );
};

// Компонент датчика заземления: иконка заземления по центру, справа — индикатор клещей или "-1" при отсутствии данных
const GroundSensor = ({ uzaStatus, dunCover, config }) => {
  const noData = uzaStatus === null || uzaStatus === undefined || uzaStatus === -1;
  const hasClamps = !noData && (uzaStatus & 0b001) !== 0;
  const hasGrounding = !noData && (uzaStatus & 0b010) !== 0;
  const coverOpen = !noData && dunCover != null && isCoverOpen(dunCover, config.dunCoverLimit);

  const clampColor = hasClamps ? '#4caf50' : coverOpen ? '#d32f2f' : '#9e9e9e';
  const iconColor = noData ? 'disabled' : hasGrounding ? 'active' : coverOpen ? 'alarm' : 'disabled';

  const groundingTooltipTitle = noData ? 'Нет данных от УЗА' : hasGrounding ? 'Заземление подключено' : 'Заземление отключено';

  const clampTooltipTitle = hasClamps ? 'Заземляющие клещи подключены' : 'Заземляющие клещи не подключены';

  return (
    <Box
      className='MuiPaperProgress-status'
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        svg: { width: '20px' },
      }}>
      <Box sx={{ flex: 1 }} />
      <Tooltip title={groundingTooltipTitle} placement='top' arrow>
        <Box sx={{ flexShrink: 0, display: 'inline-flex' }}>
          <TransportGroundSensorIcon viewBox='0 0 24 24' color={iconColor} />
        </Box>
      </Tooltip>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 0 }}>
        {noData ? (
          <Tooltip title='Нет данных от УЗА' placement='top' arrow>
            <Typography component='span' variant='caption' color='text.secondary' sx={{ cursor: 'default' }}>
              -1
            </Typography>
          </Tooltip>
        ) : (
          <Tooltip title={clampTooltipTitle} placement='top' arrow>
            <Box component='span' sx={{ display: 'inline-flex' }}>
              <ClampIndicator color={clampColor} />
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// Кнопка управления прицепом
const TrailerControlButton = ({ isActive, disabled, sensorDisabled, onToggle }) => {
  const tooltipTitle = sensorDisabled ? 'Перейдите на последнее состояние' : '';
  const color = isActive ? 'error' : 'inherit';

  return (
    <Tooltip title={tooltipTitle} placement='top' disableInteractive arrow>
      <IconButtonComponent>
        <IconButton size='small' color={color} disabled={disabled} onClick={onToggle}>
          <Build />
        </IconButton>
      </IconButtonComponent>
    </Tooltip>
  );
};
